import { createClient, fetchExchange, Client } from 'urql';
import { devtoolsExchange } from '@urql/devtools';
import { cacheExchange } from '@urql/exchange-graphcache';
import type { KeyingConfig } from '@urql/exchange-graphcache';

/**
 * Adds authorization header when available.
 * @param token JWT access token
 */
const requestOptions = (token?: string) => {
  if (!token) {
    if (process.env.IS_OFFLINE) {
      return { credentials: 'include' as const };
    }
    return {};
  } else {
    const headers = { Authorization: `Bearer ${token}` };

    // As locally we use different ports we need to include the credentials
    // otherwise cookies are not sent with Set-Cookie response header as domain different
    if (process.env.IS_OFFLINE) {
      return { headers, credentials: 'include' as const };
    }
    return { headers };
  }
};

/**
 * The cache expects items to have either `id` or `_id`. If we have any data
 * that doesn't fit this, or need other behaviour, we can specify which property
 * to use as the unique id.
 */
const keys: KeyingConfig = {};

/**
 * Things that still need to be figured out
 * - should we use `requestPolicy: 'cache-and-network'` in the client config?
 * - how to create an introspection query that'll work with this.
 *
 * Don't use the regular `cacheExchange` from urql as it is not normalised and
 * harder to work with.
 *
 * Exchanges are basically just middleware.
 */
export const generateClient = (token?: string): Client => {
  const local = `${window.location.hostname}:4000`;
  const isLocal = window.location.host.includes(':3000');
  const url = isLocal ? local : window.location.hostname;
  const apiUrl = `https://${url}/api/graphql`;
  return createClient({
    url: apiUrl,
    fetchOptions: () => requestOptions(token),
    exchanges: [
      devtoolsExchange,
      cacheExchange({ keys }),
      fetchExchange,
    ],
  });
};
